/** Limite de dessin uniquement ; les compteurs conservent la capacité géométrique complète. */
export const MAX_PALLET_PREVIEW_PACKAGES = 200

interface PalletPreviewInput {
  palletLength: number
  palletWidth: number
  boardHeight: number
  maxHeight: number
  packageLength?: number | null
  packageWidth?: number | null
  packageHeight?: number | null
}

export interface PalletPreviewPosition {
  x: number
  y: number
  z: number
  layer: number
}

/** Un aperçu borné, même si les dimensions saisies impliquent des millions de colis. */
export function buildPalletPreviewLayout(input: PalletPreviewInput) {
  const empty = (reason: 'package-dimensions' | 'pallet-dimensions' | 'count-range') => ({
    ready: false, reason, packagesPerLayer: 0, layers: 0, totalPackages: 0,
    simplified: false, positions: [] as PalletPreviewPosition[],
  })
  const positive = (value: number | null | undefined) => value != null && Number.isFinite(Number(value)) && Number(value) > 0
  if (![input.packageLength, input.packageWidth, input.packageHeight].every(positive)) return empty('package-dimensions')
  if (![input.palletLength, input.palletWidth, input.maxHeight].every(positive)
    || !Number.isFinite(input.boardHeight) || input.boardHeight < 0) return empty('pallet-dimensions')

  const length = Number(input.packageLength), width = Number(input.packageWidth), height = Number(input.packageHeight)
  const alongLength = Math.floor(input.palletLength / length)
  const alongWidth = Math.floor(input.palletWidth / width)
  const packagesPerLayer = alongLength * alongWidth
  const layers = packagesPerLayer > 0 ? Math.max(0, Math.floor((input.maxHeight - input.boardHeight) / height)) : 0
  const totalPackages = packagesPerLayer * layers
  if (![alongLength, alongWidth, packagesPerLayer, layers, totalPackages].every(Number.isSafeInteger)) return empty('count-range')

  const count = Math.min(totalPackages, MAX_PALLET_PREVIEW_PACKAGES)
  const positions: PalletPreviewPosition[] = []
  for (let index = 0; index < count; index++) {
    // Répartir l'échantillon sur toutes les couches sans parcourir les colis omis.
    const slot = totalPackages === count ? index : Math.floor((index / count) * totalPackages)
    const layer = Math.floor(slot / packagesPerLayer)
    const cell = slot % packagesPerLayer
    const row = Math.floor(cell / alongWidth), column = cell % alongWidth
    positions.push({
      x: -input.palletLength / 2 + (row + 0.5) * length,
      y: -input.palletWidth / 2 + (column + 0.5) * width,
      z: input.boardHeight + layer * height,
      layer,
    })
  }
  return { ready: true, reason: null, packagesPerLayer, layers, totalPackages,
    simplified: totalPackages > count, positions }
}
